import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
  ApiService,
  BillingLedgerResponse,
  BillingProfileResponse,
  BillingUsageSummaryResponse,
  CurrentUserProfile,
  TopupPackOption,
} from '../../shared/services/api.service';

type EditableProfileSnapshot = {
  name: string;
  surname: string;
  idNumber: string;
};

type PaystackInlineTransaction = {
  reference?: string;
  trxref?: string;
};

type PaystackInlineClient = {
  resumeTransaction: (
    accessCode: string,
    callbacks?: {
      onLoad?: (response: { accessCode?: string }) => void;
      onSuccess?: (transaction: PaystackInlineTransaction) => void;
      onCancel?: () => void;
      onError?: (error: { message?: string }) => void;
    }
  ) => void;
};

type PaystackInlineConstructor = new () => PaystackInlineClient;

type InitPaymentMethodResponse = {
  setupReference: string;
  authorizationUrl: string;
  accessCode: string;
};

@Component({
  selector: 'app-my-account',
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.css'],
})
export class MyAccountComponent implements OnInit {
  readonly profileForm: FormGroup;
  manageSubscriptionUrl: string | null = null;
  loadingManageSubscriptionUrl = false;
  loading = true;
  saving = false;
  billingProfile: BillingProfileResponse | null = null;
  billingUsage: BillingUsageSummaryResponse | null = null;
  billingLedger: BillingLedgerResponse | null = null;
  isAdmin = false;
  isBillingOwner = false;
  requiresTeamBillingSetup = false;
  isTeamUser = false;
  selectedTopupPack = 100;
  topupPackOptions: TopupPackOption[] = [];
  awaitingPaymentConfirmation = false;
  awaitingPaymentMessage = '';
  updatingCard = false;
  purchasingTopup = false;
  cancellingSubscription = false;
  reenablingSubscription = false;
  initialSnapshot: EditableProfileSnapshot | null = null;
  private readonly maxVerifyPollAttempts = 20;
  private readonly verifyPollIntervalMs = 2500;

  constructor(
    private readonly fb: FormBuilder,
    private readonly apiService: ApiService,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog
  ) {
    this.profileForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      surname: ['', [Validators.required, Validators.maxLength(100)]],
      email: [{ value: '', disabled: true }],
      idNumber: ['', [Validators.pattern(/^\d{13}$/)]],
    });
  }

  ngOnInit(): void {
    const callbackReference = String(this.route.snapshot.queryParamMap.get('paystackReference') || '').trim();
    if (callbackReference) {
      this.awaitingPaymentConfirmation = true;
      this.awaitingPaymentMessage = 'Waiting for payment confirmation...';
      this.pollSubscriptionSetupStatus(callbackReference, 1);
    }
    const topupReference = String(this.route.snapshot.queryParamMap.get('topupReference') || '').trim();
    if (topupReference) {
      this.beginAwaitingTopupConfirmation(topupReference);
    }
    const paymentMethodReference = String(
      this.route.snapshot.queryParamMap.get('paymentMethodReference') || ''
    ).trim();
    if (paymentMethodReference) {
      this.awaitingPaymentConfirmation = true;
      this.awaitingPaymentMessage = 'Waiting for card update confirmation...';
      this.pollPaymentMethodSetupStatus(paymentMethodReference, 1);
    }
    this.loadProfile();
    this.loadBilling();
    this.loadTopupPacks();
  }

  get canUpdate(): boolean {
    return !this.loading && !this.saving && this.profileForm.valid && this.hasChanges();
  }

  get displayCurrentPlan(): string {
    if (this.isTeamUser) {
      return 'Team';
    }
    return this.billingProfile?.currentPlan || '-';
  }

  get showTeamSeatBreakdown(): boolean {
    return (
      this.isAdmin &&
      this.isBillingOwner &&
      this.isTeamUser &&
      (this.billingProfile?.teamSeatBreakdown?.length || 0) > 0
    );
  }

  get teamSeatBreakdown() {
    return this.billingProfile?.teamSeatBreakdown || [];
  }

  get displayTeamMonthlyTotal(): string {
    const cents = Number(this.billingProfile?.teamMonthlyTotalCents || 0);
    return this.formatZar(cents);
  }

  get selectedTopupPackOption(): TopupPackOption | null {
    if (!this.topupPackOptions.length) {
      return null;
    }
    return this.topupPackOptions.find((pack) => pack.packSize === this.selectedTopupPack) || this.topupPackOptions[0];
  }

  formatSeatMonthlyCost(cents: number): string {
    return this.formatZar(Number(cents || 0));
  }

  formatTopupPackLabel(pack: TopupPackOption): string {
    const size = Number(pack?.packSize || 0);
    const totalAmountCents = Number(
      pack?.totalAmountCents ?? size * Number(pack?.unitPriceCents || 0)
    );
    return `${size} (${this.formatZar(totalAmountCents)})`;
  }

  private formatZar(cents: number): string {
    return `R${(Math.max(0, cents) / 100).toFixed(2)}`;
  }

  get displayNextPaymentDate(): string {
    if (this.isBillingCancelled) {
      return '-';
    }
    if (this.isPaygPlan) {
      return 'No monthly renewal — top up anytime';
    }
    if (this.isTrialPlan) {
      const raw = String(this.billingProfile?.nextPaymentDate || '').trim();
      if (!raw) return 'Trial renewal';
      const parsed = new Date(raw);
      if (Number.isNaN(parsed.getTime())) return raw;
      const formatted = new Intl.DateTimeFormat('sv-SE', {
        timeZone: 'Africa/Johannesburg',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(parsed);
      return `${formatted} SAST (trial renewal)`;
    }
    const raw = String(this.billingProfile?.nextPaymentDate || '').trim();
    if (!raw) return '-';
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    const formatted = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(parsed);
    return `${formatted} SAST`;
  }

  get isTrialPlan(): boolean {
    return String(this.billingProfile?.planCode || '').trim() === 'INDIVIDUAL_TRIAL';
  }

  get isPaygPlan(): boolean {
    return String(this.billingProfile?.planCode || '').trim() === 'INDIVIDUAL_PAYG';
  }

  get hasZeroCredits(): boolean {
    return Number(this.billingProfile?.credits?.totalRemaining || 0) <= 0;
  }

  get displayPaymentMethod(): string {
    if (this.isBillingCancelled) {
      return '-';
    }
    if (this.isPaygPlan) {
      return 'Pay as you go (no subscription)';
    }
    if (this.isTrialPlan) {
      return 'Trial (no payment required)';
    }
    if (this.isInvoiceBillingType) {
      return 'Invoice';
    }
    if (this.isBillingPaymentExempt) {
      return 'Exempt';
    }
    if (this.isTeamMemberManagedByAdmin) {
      return 'Managed by Admin';
    }
    return this.billingProfile?.paymentMethod?.masked || '-';
  }

  get isBillingCancelled(): boolean {
    return String(this.billingProfile?.billingStatus || '').trim().toUpperCase() === 'CANCELLED';
  }

  get isBillingPendingCancellation(): boolean {
    return this.billingProfile?.subscriptionPendingCancellation === true;
  }

  get displayBillingStatus(): string {
    const status = String(this.billingProfile?.billingStatus || '').trim().toUpperCase();
    if (status === 'PENDING_CANCELLATION') {
      return 'Pending cancellation';
    }
    return this.billingProfile?.billingStatus || '-';
  }

  get canReenableSubscription(): boolean {
    return this.billingProfile?.canReenableSubscription === true;
  }

  get isTeamBillingOwner(): boolean {
    return this.isTeamUser && this.isBillingOwner;
  }

  get isBillingPaymentExempt(): boolean {
    return this.isTeamBillingOwner && this.billingProfile?.billingPaymentExempt === true;
  }

  get isInvoiceBillingType(): boolean {
    return this.isTeamBillingOwner && this.billingProfile?.billingPaymentType === 'INVOICE';
  }

  get hasRecurringPaymentIssue(): boolean {
    if (this.isTrialPlan || this.isPaygPlan) return false;
    if (this.isBillingPaymentExempt || this.isInvoiceBillingType) return false;
    if (this.isTeamBillingOwner) {
      if (!this.billingProfile?.paymentMethod) return true;
      return this.billingProfile.paymentMethod.recurringReusable !== true;
    }
    if (this.isTeamUser) return false;
    if (this.billingProfile?.requiresRecurringSetup === true) return true;
    if (!this.billingProfile?.paymentMethod) return true;
    return (
      this.billingProfile.paymentMethod.recurringReusable !== true ||
      this.billingProfile.hasPaystackSubscription !== true
    );
  }

  get recurringPaymentIssueMessage(): string {
    if (this.isTeamBillingOwner) {
      return 'Current card is not valid for recurring team charges. Update card details to prevent billing failure at renewal.';
    }
    return 'Current card is not valid for recurring subscription charges. Retry card setup to keep your next renewal on schedule.';
  }

  get isTeamMemberManagedByAdmin(): boolean {
    return this.isTeamUser && !this.isBillingOwner;
  }

  get shouldShowIndividualRecurringRecovery(): boolean {
    return !this.isTeamUser && this.hasRecurringPaymentIssue;
  }

  get canCancelSubscription(): boolean {
    if (this.isTrialPlan || this.isPaygPlan) {
      return false;
    }
    if (this.isTeamMemberManagedByAdmin) {
      return false;
    }
    if (this.billingProfile?.subscriptionPendingCancellation) {
      return false;
    }
    if (this.billingProfile?.canCancelSubscription === true) {
      return true;
    }
    return this.isSelfManagedIndividualWithActiveBilling();
  }

  get showManageSubscriptionCard(): boolean {
    return !!this.billingProfile && !this.isTrialPlan && !this.isPaygPlan;
  }

  onSelectedPackChange(packSize: number): void {
    this.selectedTopupPack = packSize;
  }

  onBuyPackCredits(packSize: number): void {
    this.selectedTopupPack = packSize;
    this.onPurchaseTopup();
  }

  private isSelfManagedIndividualWithActiveBilling(): boolean {
    if (this.isTeamUser) {
      return false;
    }
    const status = String(this.billingProfile?.billingStatus || '').trim().toUpperCase();
    const hasVerifiedPaymentMethod = this.billingProfile?.paymentMethod?.verified === true;
    return hasVerifiedPaymentMethod && (status === 'ACTIVE' || status === 'PAST_DUE');
  }

  get displayExpiryDate(): string {
    const raw = String(this.billingProfile?.credits?.nextIndividualExpiryAt || '').trim();
    if (!raw) return '-';
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    return new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(parsed);
  }

  onUpdate(): void {
    if (!this.canUpdate) {
      return;
    }

    const current = this.getCurrentSnapshot();
    this.saving = true;
    this.apiService
      .updateCurrentUserProfile({
        name: current.name,
        surname: current.surname,
        idNumber: current.idNumber,
      })
      .subscribe({
        next: (response) => {
          this.initialSnapshot = {
            name: response.user.name?.trim() || '',
            surname: response.user.surname?.trim() || '',
            idNumber: response.user.idNumber?.trim() || '',
          };
          this.patchFormFromProfile(response.user);
          this.snackBar.open('Profile updated successfully', 'Close', { duration: 3000 });
        },
        error: (error) => {
          const message = error?.error?.error?.message || 'Failed to update profile';
          this.snackBar.open(message, 'Close', { duration: 5000 });
          this.saving = false;
        },
        complete: () => {
          this.saving = false;
        },
      });
  }

  onPurchaseTopup(): void {
    if (!this.selectedTopupPack || this.purchasingTopup) {
      return;
    }
    this.purchasingTopup = true;
    this.apiService.purchaseTopup(this.selectedTopupPack).subscribe({
      next: (init) => {
        void this.openTopupPaystackCheckout(init);
      },
      error: (error) => {
        this.snackBar.open(error?.error?.error?.message || 'Top-up purchase initialization failed', 'Close', { duration: 5000 });
        this.purchasingTopup = false;
      },
    });
  }

  onReenableSubscription(): void {
    if (!this.canReenableSubscription || this.reenablingSubscription) {
      return;
    }
    this.reenablingSubscription = true;
    this.apiService.reenableSubscription().subscribe({
      next: () => {
        this.snackBar.open('Subscription renewal re-enabled', 'Close', { duration: 5000 });
        this.loadBilling();
      },
      error: (error) => {
        this.snackBar.open(error?.error?.error?.message || 'Failed to re-enable subscription', 'Close', {
          duration: 5000,
        });
      },
      complete: () => {
        this.reenablingSubscription = false;
      },
    });
  }

  onCancelSubscription(): void {
    if (!this.canCancelSubscription || this.cancellingSubscription) {
      return;
    }
    const confirmed = confirm(
      'Cancel your subscription? You will keep access until the end of the current billing period, and it will not renew automatically.'
    );
    if (!confirmed) {
      return;
    }
    this.cancellingSubscription = true;
    this.apiService.cancelSubscription().subscribe({
      next: () => {
        this.snackBar.open('Subscription cancellation scheduled', 'Close', { duration: 5000 });
        this.loadBilling();
      },
      error: (error) => {
        this.snackBar.open(error?.error?.error?.message || 'Failed to cancel subscription', 'Close', {
          duration: 5000,
        });
      },
      complete: () => {
        this.cancellingSubscription = false;
      },
    });
  }

  private loadProfile(): void {
    this.loading = true;
    this.apiService.getCurrentUserProfile().subscribe({
      next: (response) => {
        const user = response.user;
        this.isAdmin = user.role === 'admin';
        this.isBillingOwner = user.isBillingOwner === true;
        this.requiresTeamBillingSetup = user.requiresTeamBillingSetup === true;
        this.isTeamUser = user.team === true;
        if (this.isTeamMemberManagedByAdmin) {
          this.manageSubscriptionUrl = null;
          this.loadingManageSubscriptionUrl = false;
        }
        this.initialSnapshot = {
          name: user.name?.trim() || '',
          surname: user.surname?.trim() || '',
          idNumber: user.idNumber?.trim() || '',
        };
        this.patchFormFromProfile(user);
      },
      error: (error) => {
        const message = error?.error?.error?.message || 'Failed to load profile';
        this.snackBar.open(message, 'Close', { duration: 5000 });
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  onResubscribe(): void {
    if (this.isTeamMemberManagedByAdmin) {
      return;
    }
    const queryParams: { mode: string; scope?: string } = { mode: 'resubscribe' };
    if (this.isTeamUser && this.isBillingOwner) {
      queryParams.scope = 'team';
    }
    void this.router.navigate(['/billing/plan-selector'], { queryParams });
  }

  onUpdateCard(): void {
    if (this.isBillingPaymentExempt || this.isInvoiceBillingType || this.updatingCard) {
      return;
    }
    const ref = this.dialog.open(UpdateCardConfirmDialogComponent, {
      width: '420px',
      disableClose: true,
      panelClass: 'update-card-confirm-dialog-panel',
    });
    ref.afterClosed().subscribe((confirmed: boolean | undefined) => {
      if (confirmed === true) {
        this.beginUpdateCardFlow();
      }
    });
  }

  private beginUpdateCardFlow(): void {
    if (this.updatingCard) return;
    this.updatingCard = true;
    this.apiService.initPaymentMethod('paystack').subscribe({
      next: (init) => {
        void this.openPaymentMethodPaystackCheckout(init);
      },
      error: (error) => {
        this.snackBar.open(error?.error?.error?.message || 'Failed to initialize card update', 'Close', {
          duration: 5000,
        });
        this.updatingCard = false;
      },
    });
  }

  private async openPaymentMethodPaystackCheckout(init: InitPaymentMethodResponse): Promise<void> {
    const accessCode = String(init?.accessCode || '').trim();
    const reference = String(init?.setupReference || '').trim();
    const authorizationUrl = String(init?.authorizationUrl || '').trim();
    if (!accessCode) {
      if (authorizationUrl) {
        window.location.assign(authorizationUrl);
        return;
      }
      this.snackBar.open('Missing Paystack access code from card update init', 'Close', { duration: 5000 });
      this.updatingCard = false;
      return;
    }

    const inlineLoaded = await this.ensurePaystackInlineLoaded();
    const PaystackCtor = this.getPaystackInlineConstructor();
    if (!inlineLoaded || !PaystackCtor) {
      if (authorizationUrl) {
        window.location.assign(authorizationUrl);
        return;
      }
      this.snackBar.open('Failed to initialize card update checkout', 'Close', { duration: 5000 });
      this.updatingCard = false;
      return;
    }

    const popup = new PaystackCtor();
    if (typeof popup.resumeTransaction !== 'function') {
      if (authorizationUrl) {
        window.location.assign(authorizationUrl);
        return;
      }
      this.snackBar.open('Failed to initialize card update checkout', 'Close', { duration: 5000 });
      this.updatingCard = false;
      return;
    }

    try {
      popup.resumeTransaction(accessCode, {
        onSuccess: (transaction) => {
          const confirmedReference = String(transaction?.reference || transaction?.trxref || reference).trim();
          if (!confirmedReference) {
            this.snackBar.open('Card update payment confirmed but reference is missing', 'Close', {
              duration: 5000,
            });
            this.updatingCard = false;
            return;
          }
          this.awaitingPaymentConfirmation = true;
          this.awaitingPaymentMessage = 'Waiting for card update confirmation...';
          this.pollPaymentMethodSetupStatus(confirmedReference, 1);
        },
        onCancel: () => {
          this.snackBar.open('Card update was cancelled.', 'Close', { duration: 4000 });
          this.updatingCard = false;
        },
        onError: (error) => {
          const message = String(error?.message || 'Paystack modal failed to load').trim();
          this.snackBar.open(message, 'Close', { duration: 6000 });
          this.updatingCard = false;
        },
      });
    } catch {
      if (authorizationUrl) {
        window.location.assign(authorizationUrl);
        return;
      }
      this.snackBar.open('Failed to initialize card update checkout', 'Close', { duration: 5000 });
      this.updatingCard = false;
    }
  }

  private loadBilling(): void {
    this.apiService.getBillingProfile().subscribe({
      next: (profile) => {
        this.billingProfile = profile;
        this.refreshManageSubscriptionAction();
      },
      error: () => {
        this.billingProfile = null;
        this.manageSubscriptionUrl = null;
        this.loadingManageSubscriptionUrl = false;
      },
    });
    this.apiService.getBillingUsageSummary().subscribe({
      next: (usage) => {
        this.billingUsage = usage;
      },
      error: () => {
        this.billingUsage = null;
      },
    });
    this.apiService.getBillingLedger(10).subscribe({
      next: (ledger) => {
        this.billingLedger = ledger;
      },
      error: () => {
        this.billingLedger = null;
      },
    });
  }

  private verifyTopupPurchase(reference: string): void {
    this.apiService.verifyTopupPurchase(reference).subscribe({
      next: () => {
        this.awaitingPaymentConfirmation = false;
        this.awaitingPaymentMessage = '';
        this.apiService.requestUserInfoSummaryRefresh();
        this.snackBar.open('Top-up purchase completed', 'Close', { duration: 4000 });
        void this.router.navigate([], {
          queryParams: { topupReference: null },
          queryParamsHandling: 'merge',
        });
        this.loadBilling();
      },
      error: (error) => {
        this.awaitingPaymentConfirmation = false;
        this.awaitingPaymentMessage = '';
        this.snackBar.open(error?.error?.error?.message || 'Failed to verify top-up payment', 'Close', {
          duration: 5000,
        });
      },
    });
  }

  private async openTopupPaystackCheckout(init: {
    reference: string;
    authorizationUrl: string;
    accessCode: string;
    publicKey: string | null;
  }): Promise<void> {
    const accessCode = String(init.accessCode || '').trim();
    const reference = String(init.reference || '').trim();
    if (!accessCode) {
      this.snackBar.open('Missing Paystack access code from top-up init', 'Close', { duration: 5000 });
      this.purchasingTopup = false;
      return;
    }

    const inlineLoaded = await this.ensurePaystackInlineLoaded();
    const PaystackCtor = this.getPaystackInlineConstructor();
    if (!inlineLoaded || !PaystackCtor) {
      window.location.assign(init.authorizationUrl);
      return;
    }

    const popup = new PaystackCtor();
    if (typeof popup.resumeTransaction !== 'function') {
      window.location.assign(init.authorizationUrl);
      return;
    }

    try {
      popup.resumeTransaction(accessCode, {
        onSuccess: (transaction) => {
          const confirmedReference = String(transaction?.reference || transaction?.trxref || reference).trim();
          if (!confirmedReference) return;
          this.beginAwaitingTopupConfirmation(confirmedReference);
        },
        onCancel: () => {
          this.snackBar.open('Top-up payment was cancelled.', 'Close', { duration: 4000 });
          this.purchasingTopup = false;
        },
        onError: (error) => {
          const message = String(error?.message || 'Paystack modal failed to load').trim();
          this.snackBar.open(message, 'Close', { duration: 6000 });
          this.purchasingTopup = false;
        },
      });
    } catch {
      window.location.assign(init.authorizationUrl);
    }
  }

  private beginAwaitingTopupConfirmation(reference: string): void {
    this.awaitingPaymentConfirmation = true;
    this.awaitingPaymentMessage = 'Waiting for top-up payment confirmation...';
    this.pollTopupPurchaseStatus(reference, 1);
  }

  private loadTopupPacks(): void {
    this.apiService.getTopupPacks().subscribe({
      next: (response) => {
        this.topupPackOptions = response.packs || [];
        if (!this.topupPackOptions.some((pack) => pack.packSize === this.selectedTopupPack)) {
          this.selectedTopupPack =
            this.topupPackOptions.find((p) => p.packSize === 100)?.packSize ||
            this.topupPackOptions[0]?.packSize ||
            50;
        }
      },
      error: () => {
        this.topupPackOptions = [];
      },
    });
  }

  private refreshManageSubscriptionAction(): void {
    if (this.isTeamMemberManagedByAdmin || this.isBillingCancelled || this.isTeamBillingOwner) {
      this.manageSubscriptionUrl = null;
      this.loadingManageSubscriptionUrl = false;
      return;
    }
    this.loadManageSubscriptionUrl();
  }

  private loadManageSubscriptionUrl(): void {
    this.loadingManageSubscriptionUrl = true;
    this.manageSubscriptionUrl = null;
    this.apiService.getSubscriptionManageLink().subscribe({
      next: (response) => {
        this.manageSubscriptionUrl = String(response?.url || '').trim() || null;
        this.loadingManageSubscriptionUrl = false;
      },
      error: () => {
        this.manageSubscriptionUrl = null;
        this.loadingManageSubscriptionUrl = false;
      },
    });
  }

  private patchFormFromProfile(user: Partial<CurrentUserProfile>): void {
    this.profileForm.patchValue(
      {
        name: user.name || '',
        surname: user.surname || '',
        email: user.email || '',
        idNumber: user.idNumber || '',
      },
      { emitEvent: false }
    );
    this.profileForm.markAsPristine();
  }

  private getCurrentSnapshot(): EditableProfileSnapshot {
    return {
      name: (this.profileForm.get('name')?.value || '').trim(),
      surname: (this.profileForm.get('surname')?.value || '').trim(),
      idNumber: (this.profileForm.get('idNumber')?.value || '').trim(),
    };
  }

  private hasChanges(): boolean {
    if (!this.initialSnapshot) {
      return false;
    }
    const current = this.getCurrentSnapshot();
    return (
      current.name !== this.initialSnapshot.name ||
      current.surname !== this.initialSnapshot.surname ||
      current.idNumber !== this.initialSnapshot.idNumber
    );
  }

  private pollSubscriptionSetupStatus(reference: string, attempt: number): void {
    this.apiService.getSubscriptionSetupStatus(reference).subscribe({
      next: (status) => {
        if (status.status === 'SUCCESS') {
          this.snackBar.open('Team billing setup completed successfully.', 'Close', { duration: 5000 });
          this.awaitingPaymentConfirmation = false;
          this.awaitingPaymentMessage = '';
          void this.router.navigate([], {
            queryParams: { paystackReference: null },
            queryParamsHandling: 'merge',
          });
          this.loadProfile();
          this.loadBilling();
          return;
        }
        if (status.status === 'FAILED') {
          this.awaitingPaymentConfirmation = false;
          this.awaitingPaymentMessage = '';
          this.snackBar.open(status.message || 'Billing payment failed', 'Close', { duration: 5000 });
          return;
        }
        if (attempt >= this.maxVerifyPollAttempts) {
          this.awaitingPaymentConfirmation = false;
          this.awaitingPaymentMessage = '';
          this.snackBar.open('Payment is still pending confirmation. Please contact support.', 'Close', {
            duration: 6000,
          });
          return;
        }
        const remaining = this.maxVerifyPollAttempts - attempt;
        this.awaitingPaymentMessage = `Waiting for payment confirmation... retrying (${remaining} left)`;
        window.setTimeout(() => this.pollSubscriptionSetupStatus(reference, attempt + 1), this.verifyPollIntervalMs);
      },
      error: (error) => {
        if (this.isRetryableVerifyError(error) && attempt < this.maxVerifyPollAttempts) {
          const remaining = this.maxVerifyPollAttempts - attempt;
          this.awaitingPaymentMessage = `Waiting for payment confirmation... retrying (${remaining} left)`;
          window.setTimeout(() => this.pollSubscriptionSetupStatus(reference, attempt + 1), this.verifyPollIntervalMs);
          return;
        }
        this.awaitingPaymentConfirmation = false;
        this.awaitingPaymentMessage = '';
        this.snackBar.open(error?.error?.error?.message || 'Failed to confirm billing payment', 'Close', {
          duration: 5000,
        });
      },
    });
  }

  private isRetryableVerifyError(error: any): boolean {
    const code = String(error?.error?.error?.code || '').trim().toUpperCase();
    return code === 'NOT_FOUND' || code === 'INTERNAL_ERROR';
  }

  private pollTopupPurchaseStatus(reference: string, attempt: number): void {
    this.apiService.getTopupPurchaseStatus(reference).subscribe({
      next: (status) => {
        if (status.status === 'SUCCESS') {
          this.awaitingPaymentConfirmation = false;
          this.awaitingPaymentMessage = '';
          this.purchasingTopup = false;
          this.apiService.requestUserInfoSummaryRefresh();
          this.snackBar.open(status.message || 'Top-up purchase completed', 'Close', { duration: 4500 });
          void this.router.navigate([], {
            queryParams: { topupReference: null },
            queryParamsHandling: 'merge',
          });
          this.loadBilling();
          return;
        }
        if (status.status === 'FAILED') {
          this.awaitingPaymentConfirmation = false;
          this.awaitingPaymentMessage = '';
          this.purchasingTopup = false;
          this.snackBar.open(status.message || 'Top-up payment failed', 'Close', { duration: 5000 });
          return;
        }
        if (attempt >= this.maxVerifyPollAttempts) {
          this.awaitingPaymentConfirmation = false;
          this.awaitingPaymentMessage = '';
          this.purchasingTopup = false;
          this.snackBar.open('Top-up payment is still pending confirmation. Please refresh shortly.', 'Close', {
            duration: 6000,
          });
          return;
        }
        const remaining = this.maxVerifyPollAttempts - attempt;
        this.awaitingPaymentMessage = `Waiting for top-up payment confirmation... retrying (${remaining} left)`;
        window.setTimeout(() => this.pollTopupPurchaseStatus(reference, attempt + 1), this.verifyPollIntervalMs);
      },
      error: (error) => {
        if (this.isRetryableVerifyError(error) && attempt < this.maxVerifyPollAttempts) {
          const remaining = this.maxVerifyPollAttempts - attempt;
          this.awaitingPaymentMessage = `Waiting for top-up payment confirmation... retrying (${remaining} left)`;
          window.setTimeout(() => this.pollTopupPurchaseStatus(reference, attempt + 1), this.verifyPollIntervalMs);
          return;
        }
        this.purchasingTopup = false;
        // Fallback reconcile call keeps old behavior for late webhook / edge cases.
        this.verifyTopupPurchase(reference);
      },
    });
  }

  private getPaystackInlineConstructor(): PaystackInlineConstructor | undefined {
    const win = window as Window & { Paystack?: PaystackInlineConstructor; PaystackPop?: PaystackInlineConstructor };
    return win.PaystackPop || win.Paystack;
  }

  private async ensurePaystackInlineLoaded(): Promise<boolean> {
    if (this.getPaystackInlineConstructor()) return true;

    return new Promise<boolean>((resolve) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-paystack-inline-v2="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(Boolean(this.getPaystackInlineConstructor())), { once: true });
        existing.addEventListener('error', () => resolve(false), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v2/inline.js';
      script.async = true;
      script.defer = true;
      script.setAttribute('data-paystack-inline-v2', 'true');
      script.onload = () => resolve(Boolean(this.getPaystackInlineConstructor()));
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  private pollPaymentMethodSetupStatus(reference: string, attempt: number): void {
    this.apiService.getPaymentMethodSetupStatus(reference).subscribe({
      next: (status) => {
        if (status.status === 'SUCCESS') {
          this.awaitingPaymentConfirmation = false;
          this.awaitingPaymentMessage = '';
          this.updatingCard = false;
          this.snackBar.open('Card details updated successfully.', 'Close', { duration: 5000 });
          void this.router.navigate([], {
            queryParams: { paymentMethodReference: null },
            queryParamsHandling: 'merge',
          });
          this.loadProfile();
          this.loadBilling();
          return;
        }
        if (status.status === 'FAILED') {
          this.awaitingPaymentConfirmation = false;
          this.awaitingPaymentMessage = '';
          this.updatingCard = false;
          this.snackBar.open(status.message || 'Card update failed', 'Close', { duration: 5000 });
          return;
        }
        if (attempt >= this.maxVerifyPollAttempts) {
          this.awaitingPaymentConfirmation = false;
          this.awaitingPaymentMessage = '';
          this.updatingCard = false;
          this.snackBar.open('Card update is still pending confirmation. Please refresh shortly.', 'Close', {
            duration: 6000,
          });
          return;
        }
        const remaining = this.maxVerifyPollAttempts - attempt;
        this.awaitingPaymentMessage = `Waiting for card update confirmation... retrying (${remaining} left)`;
        window.setTimeout(() => this.pollPaymentMethodSetupStatus(reference, attempt + 1), this.verifyPollIntervalMs);
      },
      error: (error) => {
        if (this.isRetryableVerifyError(error) && attempt < this.maxVerifyPollAttempts) {
          const remaining = this.maxVerifyPollAttempts - attempt;
          this.awaitingPaymentMessage = `Waiting for card update confirmation... retrying (${remaining} left)`;
          window.setTimeout(() => this.pollPaymentMethodSetupStatus(reference, attempt + 1), this.verifyPollIntervalMs);
          return;
        }
        this.awaitingPaymentConfirmation = false;
        this.awaitingPaymentMessage = '';
        this.updatingCard = false;
        this.snackBar.open(error?.error?.error?.message || 'Failed to confirm card update', 'Close', {
          duration: 5000,
        });
      },
    });
  }
}

@Component({
  selector: 'app-update-card-confirm-dialog',
  template: `
    <h2 mat-dialog-title>Update Card</h2>
    <mat-dialog-content>
      <p>
        We will validate your payment method to keep recurring team billing active.
      </p>
      <p>
        If your account is current, a temporary <strong>R1.00</strong> validation charge is used and refunded by the
        provider.
      </p>
      <p>
        If your billing is <strong>past due</strong>, the full outstanding renewal amount may be charged instead
        to restore active billing before recurring renewals continue.
      </p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [mat-dialog-close]="false">Cancel</button>
      <button mat-raised-button color="primary" [mat-dialog-close]="true">Proceed</button>
    </mat-dialog-actions>
  `,
})
export class UpdateCardConfirmDialogComponent {}
